import { useMemo } from 'react';
import { VAT_RATE } from '../../utils/costCalculations';

/**
 * useProjectFinancials - Computes budget totals from vendor quotes + item waterfall.
 *
 * Budget used = sum of selected quote totals (incl VAT)
 *             + optionally: monthly recurring × months remaining
 *             + optionally: estimated costs for unquoted items
 *
 * Replaces the old project_financials-based calculation.
 */
export const useProjectFinancials = ({
  vendorQuotes,
  projectItems,
  costTypes,
  currentProject,
  includeMonthlyPayments,
  includeEstimatedCosts,
}) => {
  const { budgetUsed, monthlyRecurringTotal, estimatedRecurringTotal, estimatedCostsTotal } =
    useMemo(() => {
      if (!costTypes) {
        return {
          budgetUsed: 0,
          monthlyRecurringTotal: 0,
          estimatedRecurringTotal: 0,
          estimatedCostsTotal: 0,
        };
      }

      // --- Selected quote totals (incl VAT) ---
      const selectedQuotes = (vendorQuotes || []).filter(q => q.status === 'selected');
      const quotesTotal = selectedQuotes.reduce((acc, q) => {
        const price = parseFloat(q.total_price) || 0;
        // total_price is pre-VAT; apply VAT
        return acc + price * (1 + VAT_RATE);
      }, 0);

      // --- Monthly recurring costs ---
      // Items with cost type = מחיר חודשי that have a total_cost (estimated or override)
      const monthlyCostType = costTypes.find(type => type.name === 'מחיר חודשי');
      let monthlyRecurringTotal = 0;

      (projectItems || []).forEach(item => {
        const effectiveCostTypeId = item.override_cost_type_id || item.item_type_id;
        // Check if item's cost type is monthly (via master_items.cost_type_id match)
        if (effectiveCostTypeId === monthlyCostType?.id && item.total_cost) {
          monthlyRecurringTotal += item.total_cost;
        }
      });

      // Calculate estimated recurring total based on months until project end
      let estimatedRecurringTotal = 0;
      if (currentProject?.estimated_completion) {
        const today = new Date();
        const endDate = new Date(currentProject.estimated_completion);
        const monthsUntilEnd = Math.max(
          0,
          (endDate.getFullYear() - today.getFullYear()) * 12 +
            (endDate.getMonth() - today.getMonth()),
        );
        estimatedRecurringTotal = monthlyRecurringTotal * monthsUntilEnd;
      }

      // --- Estimated costs for unquoted items ---
      // project_items without active_quote_line_id
      let estimatedTotal = (projectItems || []).reduce((acc, item) => {
        if (!item.active_quote_line_id && item.total_cost) {
          acc += item.total_cost;
        }
        return acc;
      }, 0);

      // --- Total budget used ---
      let budgetUsed = quotesTotal;
      if (includeMonthlyPayments) budgetUsed += estimatedRecurringTotal;
      if (includeEstimatedCosts) budgetUsed += estimatedTotal;

      return {
        budgetUsed,
        monthlyRecurringTotal,
        estimatedRecurringTotal,
        estimatedCostsTotal: estimatedTotal,
      };
    }, [
      vendorQuotes,
      projectItems,
      currentProject?.estimated_completion,
      includeMonthlyPayments,
      includeEstimatedCosts,
      costTypes,
    ]);

  return {
    budgetUsed,
    monthlyRecurringTotal,
    estimatedRecurringTotal,
    estimatedCompletion: currentProject?.estimated_completion,
    estimatedCostsTotal,
  };
};
